import {
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { Button, message } from 'antd'
import React, { useCallback, useRef, useState } from 'react'
import { moveCatchBoardDealStage } from 'src/utils/catchBoardApi'
import {
  buildCatchBoardMoveUndoMessage,
  CATCH_BOARD_UNDO_TOAST_DURATION_S,
  fireCatchBoardLandedConfetti,
  shouldCelebrateCatchBoardLanded,
} from 'src/utils/catchBoardCelebration'
import {
  applyCatchBoardDealMove,
  findCatchBoardDealContainer,
  isCatchBoardClosedDropBlocked,
  resolveCatchBoardDropContainer,
  resolveCatchBoardDropIndex,
} from 'src/utils/catchBoardDragHelpers'

/**
 * @dnd-kit wiring for Catch Board — cross-column move + within-column reorder.
 * After persist: undo toast (5s); confetti when a deal newly lands.
 */
export function useCatchBoardDragDrop({ board, setBoard }) {
  const [activeDealId, setActiveDealId] = useState(null)
  const [isPersisting, setIsPersisting] = useState(false)
  const dragOriginRef = useRef(null)
  const cancelConfettiRef = useRef(null)
  const undoTokenRef = useRef(0)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  )

  const activeDeal =
    activeDealId == null
      ? null
      : board.deals.find(deal => String(deal.dealId) === String(activeDealId)) ||
        null

  const showMoveUndoToast = useCallback(
    ({ dealId, fromStage, fromPosition, toStage, originBoard }) => {
      const toastKey = `catch-board-undo-${dealId}`
      const token = ++undoTokenRef.current
      const copy = buildCatchBoardMoveUndoMessage({ fromStage, toStage })

      const undoMove = async () => {
        if (token !== undoTokenRef.current) {
          return
        }
        message.destroy(toastKey)
        setBoard(originBoard)
        setIsPersisting(true)
        try {
          await moveCatchBoardDealStage(dealId, {
            stage: fromStage,
            position: fromPosition,
          })
        } catch (error) {
          message.error(error.message || 'Failed to undo move')
        } finally {
          setIsPersisting(false)
        }
      }

      message.success({
        key: toastKey,
        duration: CATCH_BOARD_UNDO_TOAST_DURATION_S,
        content: (
          <span data-testid='catch-board-move-toast'>
            {copy}
            {' — '}
            <Button
              type='link'
              size='small'
              data-testid='catch-board-undo-move'
              onClick={undoMove}
              style={{ padding: 0, height: 'auto', lineHeight: 'inherit' }}
            >
              Undo
            </Button>
          </span>
        ),
      })
    },
    [setBoard],
  )

  const handleDragStart = useCallback(
    event => {
      const dealId = String(event.active.id)
      setActiveDealId(dealId)
      dragOriginRef.current = {
        board,
        dealId,
        fromStage: findCatchBoardDealContainer(board.columns, dealId),
      }
    },
    [board],
  )

  const handleDragOver = useCallback(
    event => {
      const { active, over } = event
      if (!over) {
        return
      }

      const dealId = String(active.id)
      const overId = String(over.id)
      const fromStage = findCatchBoardDealContainer(board.columns, dealId)
      const toStage = resolveCatchBoardDropContainer(board.columns, overId)

      if (!fromStage || !toStage || fromStage === toStage) {
        return
      }

      if (isCatchBoardClosedDropBlocked(fromStage, toStage)) {
        return
      }

      const toIndex = resolveCatchBoardDropIndex(board.columns, toStage, overId)
      setBoard(prev =>
        applyCatchBoardDealMove(prev, {
          dealId,
          toStage,
          toIndex,
        }),
      )
    },
    [board.columns, setBoard],
  )

  const handleDragEnd = useCallback(
    async event => {
      const { active, over } = event
      const origin = dragOriginRef.current
      setActiveDealId(null)
      dragOriginRef.current = null

      if (!over || !origin?.dealId) {
        if (origin?.board) {
          setBoard(origin.board)
        }
        return
      }

      const dealId = String(active.id)
      const overId = String(over.id)
      const fromStage = origin.fromStage
      let nextBoard = board
      const toStage = resolveCatchBoardDropContainer(nextBoard.columns, overId)

      if (!toStage) {
        setBoard(origin.board)
        return
      }

      if (isCatchBoardClosedDropBlocked(fromStage, toStage)) {
        message.info('Open the deal to mark it as Landed or Got Away')
        setBoard(origin.board)
        return
      }

      const toIndex = resolveCatchBoardDropIndex(
        nextBoard.columns,
        toStage,
        overId,
      )
      nextBoard = applyCatchBoardDealMove(nextBoard, {
        dealId,
        toStage,
        toIndex,
      })
      setBoard(nextBoard)

      const originDeal = origin.board.deals.find(
        deal => String(deal.dealId) === String(dealId),
      )
      const fromPosition = Number(originDeal?.position) || 0
      const moved = nextBoard.deals.find(
        deal => String(deal.dealId) === String(dealId),
      )
      const nextPosition = Number(moved?.position) || 0
      const nextStage = moved?.stage || toStage

      const unchanged = fromStage === nextStage && fromPosition === nextPosition

      if (unchanged) {
        return
      }

      setIsPersisting(true)
      try {
        await moveCatchBoardDealStage(dealId, {
          stage: nextStage,
          position: nextPosition,
        })

        showMoveUndoToast({
          dealId,
          fromStage,
          fromPosition,
          toStage: nextStage,
          originBoard: origin.board,
        })

        if (shouldCelebrateCatchBoardLanded(fromStage, nextStage)) {
          if (cancelConfettiRef.current) {
            cancelConfettiRef.current()
          }
          cancelConfettiRef.current = fireCatchBoardLandedConfetti()
        }
      } catch (error) {
        message.error(error.message || 'Failed to move deal')
        setBoard(origin.board)
      } finally {
        setIsPersisting(false)
      }
    },
    [board, setBoard, showMoveUndoToast],
  )

  const handleDragCancel = useCallback(() => {
    const origin = dragOriginRef.current
    setActiveDealId(null)
    dragOriginRef.current = null
    if (origin?.board) {
      setBoard(origin.board)
    }
  }, [setBoard])

  return {
    sensors,
    activeDealId,
    activeDeal,
    isPersisting,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  }
}
